import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import POLineItemTable from "@/components/POLineItemTable";
import { STATUS_STYLES } from "@/lib/format";
import { Plus, Upload, Search, Loader2, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY_FORM = { po_number: "", supplier_id: "", item: "", quantity: "", value: "", po_date: "", required_delivery_date: "", delivery_location: "", notes: "" };
const SORT_OPTIONS = [
  ["updated_at:desc", "Recently updated"], ["po_number:asc", "PO Number ↑"], ["po_number:desc", "PO Number ↓"],
  ["po_date:desc", "PO Date (newest)"], ["value:desc", "Value (high→low)"], ["expected_delivery_date:asc", "Expected delivery (soonest)"],
];

export default function PurchaseOrders() {
  const [data, setData] = useState({ pos: [], total: 0, page: 0, page_size: 15 });
  const [suppliers, setSuppliers] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [filters, setFilters] = useState({ status: "all", supplier_id: "all", transport_mode: "all", flag: "all", buyer: "all" });
  const [deliveryTo, setDeliveryTo] = useState("");
  const [sort, setSort] = useState({ key: "updated_at", order: "desc" });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page: String(page), page_size: "15", sort: sort.key, order: sort.order });
    const qv = params.get("q");
    if (qv) qs.set("q", qv);
    if (filters.status !== "all") qs.set("status", filters.status);
    if (filters.supplier_id !== "all") qs.set("supplier_id", filters.supplier_id);
    if (filters.transport_mode !== "all") qs.set("transport_mode", filters.transport_mode);
    if (filters.buyer !== "all") qs.set("buyer", filters.buyer);
    if (filters.flag === "overdue") qs.set("overdue", "true");
    if (filters.flag === "delayed") qs.set("delayed", "true");
    if (filters.flag === "pending_action") qs.set("pending_action", "true");
    if (deliveryTo) qs.set("delivery_to", deliveryTo);
    api.get(`/pos?${qs}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load POs"));
  }, [params, filters, sort, page, deliveryTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data.suppliers)).catch(() => {}); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setParams(q.trim() ? { q: q.trim() } : {});
  };

  const createPO = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/pos", { ...form, quantity: Number(form.quantity) || 0, value: Number(form.value) || 0 });
      toast.success("PO created — supplier notification logged (WhatsApp + email)");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div data-testid="po-list-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Purchase Orders</h1>
          <p className="text-sm text-muted-foreground">{data.total} POs · single source of truth for every order</p>
        </div>
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pos-export`} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="export-pos-btn"><Download className="mr-1.5 h-4 w-4" /> Export</Button>
          </a>
          <Link to="/buyer/import"><Button variant="outline" data-testid="import-pos-btn"><Upload className="mr-1.5 h-4 w-4" /> Import CSV/Excel</Button></Link>
          <Button data-testid="new-po-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus className="mr-1.5 h-4 w-4" /> New PO</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
        <form onSubmit={onSearch} className="relative min-w-64 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="po-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PO, material code, description, supplier, tracking…" className="pl-9" />
        </form>
        <Select value={filters.status} onValueChange={(v) => { setPage(1); setFilters({ ...filters, status: v }); }}>
          <SelectTrigger data-testid="po-status-filter" className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.supplier_id} onValueChange={(v) => { setPage(1); setFilters({ ...filters, supplier_id: v }); }}>
          <SelectTrigger data-testid="po-supplier-filter" className="w-48"><SelectValue placeholder="Supplier" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All suppliers</SelectItem>
            {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.transport_mode} onValueChange={(v) => { setPage(1); setFilters({ ...filters, transport_mode: v }); }}>
          <SelectTrigger data-testid="po-transport-filter" className="w-36"><SelectValue placeholder="Transport" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All transport</SelectItem>
            {["road", "rail", "air", "sea", "courier", "other"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.flag} onValueChange={(v) => { setPage(1); setFilters({ ...filters, flag: v }); }}>
          <SelectTrigger data-testid="po-flag-filter" className="w-48"><SelectValue placeholder="Quick filter" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">No quick filter</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="delayed">Delayed</SelectItem>
            <SelectItem value="pending_action">Pending Supplier Action</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">Expected before</Label>
          <Input data-testid="po-delivery-to-filter" type="date" value={deliveryTo} onChange={(e) => { setPage(1); setDeliveryTo(e.target.value); }} className="w-40" />
        </div>
        <Select value={`${sort.key}:${sort.order}`} onValueChange={(v) => { const [key, order] = v.split(":"); setPage(1); setSort({ key, order }); }}>
          <SelectTrigger data-testid="po-sort-select" className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {SORT_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <POLineItemTable pos={data.pos} linkBase="/buyer/pos" testPrefix="po-table" rowTestPrefix="po-row" />

      <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-2.5 text-sm">
        <p className="text-muted-foreground">Page {data.page || 1} of {totalPages} · {data.total} POs</p>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" data-testid="page-prev" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" data-testid="page-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader><DialogTitle className="font-heading">Create Purchase Order</DialogTitle></DialogHeader>
          <form onSubmit={createPO} className="grid grid-cols-2 gap-4">
            <div>
              <Label>PO Number *</Label>
              <Input data-testid="po-form-number" required value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="PO-2026-1031" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label>Supplier *</Label>
              <Select required value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger data-testid="po-form-supplier" className="mt-1.5"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Item / Product *</Label>
              <Input data-testid="po-form-item" required value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="CNC Machined Shaft EN24" className="mt-1.5" />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input data-testid="po-form-qty" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>PO Value (₹)</Label>
              <Input data-testid="po-form-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>PO Date *</Label>
              <Input data-testid="po-form-date" type="date" required value={form.po_date} onChange={(e) => setForm({ ...form, po_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Required Delivery Date *</Label>
              <Input data-testid="po-form-delivery" type="date" required value={form.required_delivery_date} onChange={(e) => setForm({ ...form, required_delivery_date: e.target.value })} className="mt-1.5" />
            </div>
            <div className="col-span-2">
              <Label>Delivery Location</Label>
              <Input data-testid="po-form-location" value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} placeholder="Shakti Works - Chakan, Pune" className="mt-1.5" />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input data-testid="po-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="po-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create & Notify Supplier
              </Button>
            </DialogFooter>
          </form>
          <p className="text-xs text-muted-foreground">Single-line POs are stored as one line item automatically; multi-line imports and partial delivery are supported by the data model.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
